import React, { useCallback, useMemo } from 'react';
import { Box, CheckBox, Image, Text, useDraggableList } from '@common-fe/common-fe';

import { Document } from '@/common/types';
import { DIVIDING_MODE_GROUP_PREFIX } from '@/modules/employer/components/Lockbox/lockbox.types';
import { useLockboxDividingModeStore } from '@/modules/employer/components/Lockbox/LockboxDetails/stores/useLockboxDividingMode.store';

import { CheckedImageWrapper, Title, TitleContainer } from './DividingModeImageCheckbox.styles';

interface Props {
  pageNumber: string;
  document: Document;
  children?: React.ReactNode;
  isMultiPageViewMode?: boolean
}

const DividingModeImageCheckbox: React.FC<Props> = ({ pageNumber, document, children, isMultiPageViewMode }) => {
  const { groups, activeGroupKey, setGroups } = useLockboxDividingModeStore();
  const { addItem, removeItem } = useDraggableList({ groups, setGroups, newGroupPrefix: DIVIDING_MODE_GROUP_PREFIX });

  const itemGroup = useMemo(() => {
    return groups?.find((group) => group?.items?.find((item) => item.key === pageNumber));
  }, [groups, pageNumber]);

  const handleCheck = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    const isChecked = event?.target?.checked;

    if (isChecked) {
      addItem(activeGroupKey, {
        key: pageNumber,
        node: (
          <Image
            key={pageNumber}
            src={document.blob}
            style={{ width: '100%', height: '100%', objectFit: 'contain' }} 
          />
        )
      });
      return;
    }

    removeItem(pageNumber);
  }, [addItem, removeItem, activeGroupKey, document, pageNumber]);

  return (
    <Box
      data-testId="dividing-mode-image-checkbox-wrapper"
      direction={isMultiPageViewMode ? 'column-reverse' : 'row'}
    >
      <Box
        gap="spacing12"
        {...isMultiPageViewMode ? {
          direction: 'row',
          justify: 'center',
          align: 'center',
          pad: { top: 'spacing12'}
        } : {
          pad: { horizontal: 'spacing12' },
          align: 'center',
        }}
      >
        <Box
          data-testId="dividing-mode-image-number"
          background="background3"
          round="button2Round"
          justify="center"
          align="center"
          height={{ min: '20px', height: '20px' }}
          width={{  min: '20px', width: '20px' }}
        >
          <Text color="textOnColor">
            {pageNumber}
          </Text>
        </Box>
        <CheckBox checked={!!itemGroup} onChange={handleCheck} />
      </Box>

      <Box style={{ position: 'relative', width: 'fit-content' }}>
        {children}
        {!!itemGroup && (
          <CheckedImageWrapper>
            <TitleContainer>
              <Title color="textSecondary">
                {itemGroup?.title}
              </Title>
            </TitleContainer>
          </CheckedImageWrapper>
        )}
      </Box>
    </Box>
  );
};

export default DividingModeImageCheckbox;
